import React, { FC, useEffect, useState } from "react";
import { Stack, styled, Typography } from "@mui/material";
import { TopPage } from "@/components/elements/TopPage";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import SaveIcon from "@mui/icons-material/Save";
import { AppRoutes } from "@/routes/config";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE, API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { importTalentsApi, useTalentOptions } from "@/api/services/main/talent";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { TalentAfterImport } from "@/features/talent/components/talent-import/TalentAfterImport";
import { divider, orange, state } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { dayjs } from "@/utils/dayjs";
import { useDialog } from "@/hooks/useDialog";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { sanitizeInput } from "@/utils/pieces";

type RowError = {
  row: number;
  messages: string[];
};

type TTalent = {
  fullName: string;
  birthday?: string | null;
};

export const TalentImportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { talents } = location.state || {};
  const totalRow = talents?.length ?? 0;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const _navigator = useNavigate();
  const backModal = useDialog();
  const { options: listTalent, isLoading } = useTalentOptions();
  const [errors, setErrors] = useState<RowError[]>([]);

  const checkImport = (talents: TTalent[], dbList: { fullName: string; birthday: string }[]) => {
    const errorsByRow: RowError[] = [];

    const keyToIndices: Record<string, number[]> = {};

    talents.forEach((t, idx) => {
      const { fullName, birthday } = t;
      if (!fullName || !birthday) {
      }
      const key = `${fullName}__${dayjs(birthday).format("YYYY-MM-DD")}`;
      if (!keyToIndices[key]) keyToIndices[key] = [];
      keyToIndices[key].push(idx);
    });

    talents.forEach((talent, idx) => {
      const rowNum = idx + 1;
      const { fullName, birthday } = talent;
      if (!fullName || !birthday) {
        return;
      }

      const list: string[] = [];
      const key = `${fullName}__${dayjs(birthday).format("YYYY-MM-DD")}`;
      const indices = keyToIndices[key];

      if (indices && indices.length > 1) {
        const rows = indices.map((i) => `${i + 1}行目`).join("、");
        list.push(`${rows}の項目「氏名」（${fullName}）および「生年月日」（${birthday}）が重複しています。`);
      }

      const existsInDB = dbList.some((x) => {
        if (
          sanitizeInput(String(x.fullName)).toUpperCase() === sanitizeInput(String(fullName)).toUpperCase() &&
          x.birthday === dayjs(birthday).format("YYYY-MM-DD")
        )
          return true;
      });
      if (existsInDB) {
        list.push(`${rowNum}行目の項目「氏名」（${fullName}）および「生年月日」（${birthday}）が既に登録されています。`);
      }

      if (list.length > 0) {
        errorsByRow.push({ row: rowNum, messages: list });
      }
    });

    const listError = errorsByRow.map((item) => ({
      row: item.row,
      messages: Array.from(new Set(item.messages)),
    }));

    const seen = new Set<string>();

    return listError.map((item) => {
      const uniqueList: string[] = [];

      for (const msg of item.messages) {
        if (!seen.has(msg)) {
          seen.add(msg);
          uniqueList.push(msg);
        }
      }

      return {
        row: item.row,
        messages: uniqueList,
      };
    });
  };

  useEffect(() => {
    if (!isLoading) {
      const listTalentDB = listTalent.map((talent) => ({
        fullName: talent.fullName,
        birthday: dayjs(talent.birthday).format("YYYY-MM-DD"),
      }));
      const newErrors = checkImport(talents, listTalentDB);
      setErrors(newErrors);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!talents) navigate(AppRoutes.talentList);
  });

  const handleSaveData = async () => {
    if (talents.length === 0) return;
    setIsSubmitting(true);
    try {
      await importTalentsApi(talents);
      toast.success(API_SUCCESS_MESSAGE);
      navigate(AppRoutes.talentList);
    } catch (error) {
      const message = (error as Error).message;
      toast.error(message ?? API_UNKNOWN_ERROR_MESSAGE);
    }
    setIsSubmitting(false);
  };

  const TitleInnerBox: FC = () => {
    return (
      <Typography variant="sub16Semi">
        ファイルアップロード結果：<Typography variant="head20Semi">{totalRow}</Typography> 件
      </Typography>
    );
  };
  return (
    <>
      <PageWrapper>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <TopPage title="複数人材登録" subTitle="ファイルからアップロードされた複数の人材" enablePageBack={false} />
        </Stack>
        <Content>
          <InnerBox borderColor={divider.high} padding={3} mt={2} border="none" showDivider={false} title={<TitleInnerBox />}>
            <Stack flex={1} gap={3}>
              <TalentAfterImport rows={talents ?? []} isLoading={false} />
            </Stack>
            <Stack
              sx={{ mt: "32px", p: "8px", background: state.incomplete_secondary, color: orange[80], maxHeight: "160px", overflowY: "auto" }}
              borderRadius={rounded.xs}
            >
              {errors.map((err, index) => (
                <Typography key={index} component="p" variant="cap12">
                  {err.messages.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </Typography>
              ))}
              <Typography component="p" variant="cap12">
                修正が必要な場合は修正後に再インポートしてください。
              </Typography>
            </Stack>
          </InnerBox>
        </Content>

        <FooterContainer>
          <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
            <Button variant="ghost" onClick={() => backModal.show()}>
              戻る
            </Button>
            <Stack direction="row" gap={3}>
              <Button variant="primary" disabled={isSubmitting} onClick={handleSaveData} startIcon={<SaveIcon />}>
                一括保存
              </Button>
            </Stack>
          </Stack>
        </FooterContainer>
      </PageWrapper>
      <ConfirmSaveModal open={backModal.open} onOk={() => navigate(AppRoutes.talentList)} onClose={backModal.cancel}>
        <Typography component="p" variant="subtitle1">
          インポートが終了しておりませんが戻ってもよろしいですか？
        </Typography>
      </ConfirmSaveModal>
    </>
  );
};

const PageWrapper = styled(Stack)`
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;

const Content = styled(Stack)`
  flex: 1;
`;
